import {Slot} from '@radix-ui/react-slot'
import {type PropsWithChildren} from 'react'
import {tv, type VariantProps} from 'tailwind-variants'
import {type ExtractStrict} from 'type-zoo'
import {gridItem, TextTag} from '~/design-system/foundations'
import {HgTextLink} from '~/design-system/hg/components'
import {cn} from '~/design-system/utils'

const headingVariants = tv({
  base: 'text-text-default',
  variants: {
    tag: {
      h2: 'arcadia-heading-5',
      h3: 'arcadia-heading-7',
      h4: 'arcadia-heading-9',
    },
  },
})

type Tag = NonNullable<VariantProps<typeof headingVariants>['tag']>

type HgLinkableHeadingTag = ExtractStrict<Tag, 'h2'>

type HgLinkableHeadingTagProps = {
  tag: HgLinkableHeadingTag
  id: string
}

type HgStandardHeadingTagProps = {
  tag: Exclude<Tag, HgLinkableHeadingTag>
}

type HgHeadingSpecificProps = HgLinkableHeadingTagProps | HgStandardHeadingTagProps

export type HeadingTagProps = PropsWithChildren<{
  className?: string
}> &
  HgHeadingSpecificProps

export const HeadingTag = ({
  className,
  children,
  tag,
  ...props
}: HeadingTagProps) => {
  return 'id' in props && tag === 'h2' ? (
    <HgTextLink
      className={cn(className, 'inline-flex')}
      href={`#${props.id}`}
      variant="revealUnderline"
    >
      <TextTag
        tag={tag}
        className={headingVariants({
          tag,
          className: 'scroll-mt-blog-navbars-height',
        })}
        id={props.id}
      >
        {children}
      </TextTag>
    </HgTextLink>
  ) : (
    <TextTag className={headingVariants({tag, className})} tag={tag}>
      {children}
    </TextTag>
  )
}

export const HgWYSIWYGHeading = (props: HeadingTagProps) => {
  return (
    <Slot className={gridItem({size: 'heading', className: props.className})}>
      <HeadingTag {...props} />
    </Slot>
  )
}
